import { useLocale, useTranslations } from 'next-intl';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Link } from 'react-transition-progress/next';
import { Header } from '@/components/layout/header';
import { ModalPortal } from '@/components/layout/modals/ModalPortal';
import { Button } from '@/components/ui/button/Button';
import CheckBox from '@/components/ui/input/CheckBox';
import InputWithLabel from '@/components/ui/input/InputWithLabel';
import { PATHS } from '@/constants/path';
import { REGEXP_KATAKANA } from '@/constants/regexp';
import { cn } from '@/utils/cn';
import { containsValidJISX0208Characters } from '@/utils/jis-x-0208';

type ProfileFormValues = {
  realFamilyName: string;
  realFirstName: string;
  realFamilyNameKana: string;
  realFirstNameKana: string;
  receiveNewsletter: boolean;
  agreeToTerms: boolean;
};

type Props = {
  isOpen: boolean;
  onSubmit: (data: ProfileFormValues) => Promise<void>;
};

export const MigrationProfileModal = ({ isOpen, onSubmit }: Props) => {
  const t = useTranslations();
  const locale = useLocale();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    defaultValues: {
      realFamilyName: '',
      realFirstName: '',
      realFamilyNameKana: '',
      realFirstNameKana: '',
      receiveNewsletter: true,
      agreeToTerms: false,
    },
  });

  const watchedAgreeToTerms = watch('agreeToTerms');
  const watchedRealFamilyName = watch('realFamilyName');
  const watchedRealFirstName = watch('realFirstName');
  const watchedRealFamilyNameKana = watch('realFamilyNameKana');
  const watchedRealFirstNameKana = watch('realFirstNameKana');

  const isAllFilled = Boolean(
    watchedAgreeToTerms &&
      watchedRealFamilyName &&
      watchedRealFirstName &&
      (locale === 'ja' ? watchedRealFamilyNameKana && watchedRealFirstNameKana : true),
  );

  const registerName = (name: 'realFamilyName' | 'realFirstName') => {
    return {
      ...register(name, {
        required: t('common-error-none'),
        maxLength: { value: 10, message: t('sign_up-profile-error-name-letters') },
        validate: (data) => {
          if (!containsValidJISX0208Characters(data)) {
            return t('common-error-ng_word');
          }
        },
      }),
    };
  };

  const registerKanaName = (name: 'realFamilyNameKana' | 'realFirstNameKana') => {
    return {
      ...register(name, {
        required: t('common-error-none'),
        maxLength: { value: 10, message: t('sign_up-profile-error-name-letters') },
        pattern: {
          value: REGEXP_KATAKANA,
          message: t('sign_up-profile-error-name-kana'),
        },
      }),
    };
  };

  const handleFormSubmit: SubmitHandler<ProfileFormValues> = async (data) => {
    await onSubmit(data);
  };

  return (
    <ModalPortal isOpen={isOpen} withHeaderLayout={true}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className='w-full'>
        <Header logo bgVariant='gray-deep' />
        <div className='flex flex-col gap-8 px-5 py-9'>
          <div className='flex flex-col items-center gap-3'>
            <span className='text-head-24 text-kl-black-deep'>
              {t('livenode-id-migration-profile_title')}
            </span>
            <span className='text-body-14 text-kl-black-deep'>
              {t('livenode-id-migration-profile_description')}
            </span>
          </div>
          <div
            className={cn(
              'space-y-6 rounded-lg border border-kl-gray-subtle p-5',
              isAllFilled ? 'bg-white' : 'bg-func-primary/10',
            )}
          >
            <InputWithLabel
              type='text'
              label={t('sign_up-profile-last_name')}
              required
              requiredVariant='badge'
              placeholder={t('sign_up-profile-last_name-placeholder')}
              register={registerName('realFamilyName')}
              error={errors.realFamilyName}
              textSize='text-label-14'
            />
            <InputWithLabel
              type='text'
              label={t('sign_up-profile-first_name')}
              required
              requiredVariant='badge'
              placeholder={t('sign_up-profile-first_name-placeholder')}
              register={registerName('realFirstName')}
              error={errors.realFirstName}
              textSize='text-label-14'
            />
            {locale === 'ja' && (
              <>
                <InputWithLabel
                  type='text'
                  label={t('sign_up-profile-last_name-kana')}
                  required
                  requiredVariant='badge'
                  placeholder={t('sign_up-profile-last_name-kana-placeholder')}
                  register={registerKanaName('realFamilyNameKana')}
                  error={errors.realFamilyNameKana}
                  textSize='text-label-14'
                />
                <InputWithLabel
                  type='text'
                  label={t('sign_up-profile-first_name-kana')}
                  required
                  requiredVariant='badge'
                  placeholder={t('sign_up-profile-first_name-kana-placeholder')}
                  register={registerKanaName('realFirstNameKana')}
                  error={errors.realFirstNameKana}
                  textSize='text-label-14'
                />
              </>
            )}

            <CheckBox register={register('receiveNewsletter')} size='lg'>
              <span className='text-label-14'>{t('sign_up-profile-check-recommendation')}</span>
            </CheckBox>
            <CheckBox register={register('agreeToTerms', { required: true })} size='lg'>
              <span className='text-label-14'>
                {t.rich('livenode-id-migration-profile-check-terms', {
                  term_link: (value) => (
                    // TODO:利用規約の内容が変更する可能性あり、一旦現状(2026/4)のものに遷移させる
                    <Link href={PATHS.TERMS} target='_blank' rel='noopener noreferrer'>
                      {value}
                    </Link>
                  ),
                  guideline_link: (value) => (
                    <Link href={PATHS.GUIDE_LINE} target='_blank' rel='noopener noreferrer'>
                      {value}
                    </Link>
                  ),
                })}
              </span>
            </CheckBox>
          </div>
          <div className='px-5 py-6'>
            <Button
              type='submit'
              label={t('sign_up-complete-start')}
              disabled={!isAllFilled || isSubmitting}
              color='gradient'
              fullWidth
            />
          </div>
        </div>
      </form>
    </ModalPortal>
  );
};
